import type {
  BlogMedia,
  BlogPost,
  BlogStructuredContent,
  BlogStructuredDirection,
  BlogStructuredIngredientSection,
  BlogStructuredStat,
} from "../types";

const IMAGE_MEDIA_PATTERN = /\.(png|jpe?g|gif|webp|avif|svg)(\?|#|$)/i;

function toNullableText(value?: string | null): string | null {
  if (typeof value !== "string") {
    return null;
  }
  const trimmed = value.trim();
  return trimmed ? trimmed : null;
}

export function createEmptyRecipeStructuredContent(): BlogStructuredContent {
  return {
    displayStyle: "RECIPE",
    servingText: null,
    summaryTitle: null,
    summaryBody: null,
    stats: [],
    ingredients: [],
    directions: [],
    notes: [],
  };
}

export function resolveBlogDisplayStyle(
  blog?: Pick<BlogPost, "structuredContent"> | null,
): BlogStructuredContent["displayStyle"] {
  const normalized = (blog?.structuredContent?.displayStyle || "").trim().toUpperCase();
  return normalized === "RECIPE" ? "RECIPE" : "ARTICLE";
}

export function isImageMedia(entry?: BlogMedia | null): boolean {
  const type = (entry?.type || "").trim().toUpperCase();
  if (type === "IMAGE") {
    return true;
  }
  const url = (entry?.url || "").trim().toLowerCase();
  return IMAGE_MEDIA_PATTERN.test(url);
}

export function resolveMediaAltText(entry: BlogMedia | null | undefined, fallback: string): string {
  return (entry?.alt_text || "").trim() || fallback;
}

export function resolveBlogHeroMedia(
  blog?: Pick<BlogPost, "hero_media" | "media" | "structuredContent"> | null,
): BlogMedia | null {
  if (!blog) {
    return null;
  }

  const media = Array.isArray(blog.media) ? blog.media : [];
  const firstMedia = media[0] ?? null;
  const heroMedia = blog.hero_media && isImageMedia(blog.hero_media) ? blog.hero_media : null;
  const firstImage = media.find((entry) => isImageMedia(entry)) ?? null;

  if (resolveBlogDisplayStyle(blog) === "RECIPE") {
    if (firstMedia && isImageMedia(firstMedia)) {
      return firstMedia;
    }
    return firstImage ?? heroMedia;
  }

  if (firstMedia && isImageMedia(firstMedia)) {
    return firstMedia;
  }
  return heroMedia ?? firstImage;
}

function structuredContentTextFragments(structuredContent?: BlogStructuredContent | null): string[] {
  if (!structuredContent) {
    return [];
  }

  const fragments: string[] = [];

  if (structuredContent.servingText) {
    fragments.push(structuredContent.servingText);
  }
  if (structuredContent.summaryTitle) {
    fragments.push(structuredContent.summaryTitle);
  }
  if (structuredContent.summaryBody) {
    fragments.push(structuredContent.summaryBody);
  }

  for (const stat of structuredContent.stats || []) {
    if (stat.label) {
      fragments.push(stat.label);
    }
    if (stat.value) {
      fragments.push(stat.value);
    }
    if (stat.secondaryValue) {
      fragments.push(stat.secondaryValue);
    }
  }

  for (const section of structuredContent.ingredients || []) {
    if (section.title) {
      fragments.push(section.title);
    }
    fragments.push(...section.items);
  }

  for (const direction of structuredContent.directions || []) {
    if (direction.title) {
      fragments.push(direction.title);
    }
    if (direction.body) {
      fragments.push(direction.body);
    }
    if (direction.detail) {
      fragments.push(direction.detail);
    }
  }

  fragments.push(...(structuredContent.notes || []));

  return fragments.filter((entry) => Boolean(entry && entry.trim()));
}

export function structuredContentToPlainText(structuredContent?: BlogStructuredContent | null): string {
  return structuredContentTextFragments(structuredContent).join("\n");
}

export function estimateBlogReadingTimeLabel(
  blog: Pick<BlogPost, "body" | "reading_time_minutes" | "structuredContent">,
): string {
  const explicit = blog.reading_time_minutes;
  if (explicit && explicit > 0) {
    return `${Math.max(1, Math.round(explicit))} min read`;
  }

  const source = (blog.body || "").trim() || structuredContentToPlainText(blog.structuredContent).trim();
  const wordCount = source.split(/\s+/).filter(Boolean).length;
  if (wordCount === 0) {
    return "Quick read";
  }
  return `${Math.max(1, Math.ceil(wordCount / 220))} min read`;
}

export function sanitizeStructuredContentForSave(structuredContent: BlogStructuredContent): BlogStructuredContent {
  const stats: BlogStructuredStat[] = (structuredContent.stats || [])
    .map((stat) => ({
      id: stat.id,
      label: (toNullableText(stat.label) || "").trim(),
      value: (toNullableText(stat.value) || "").trim(),
      secondaryValue: toNullableText(stat.secondaryValue),
    }))
    .filter((stat) => Boolean(stat.id) && Boolean(stat.label || stat.value || stat.secondaryValue));

  const ingredients: BlogStructuredIngredientSection[] = (structuredContent.ingredients || [])
    .map((section) => ({
      id: section.id,
      title: toNullableText(section.title),
      items: (section.items || []).map((item) => item.trim()).filter(Boolean),
    }))
    .filter((section) => Boolean(section.id) && section.items.length > 0);

  const directions: BlogStructuredDirection[] = (structuredContent.directions || [])
    .map((direction) => ({
      id: direction.id,
      title: toNullableText(direction.title),
      body: (toNullableText(direction.body) || "").trim(),
      detail: toNullableText(direction.detail),
    }))
    .filter((direction) => Boolean(direction.id) && Boolean(direction.title || direction.body || direction.detail));

  return {
    displayStyle: "RECIPE",
    servingText: toNullableText(structuredContent.servingText),
    summaryTitle: toNullableText(structuredContent.summaryTitle),
    summaryBody: toNullableText(structuredContent.summaryBody),
    stats,
    ingredients,
    directions,
    notes: (structuredContent.notes || []).map((note) => note.trim()).filter(Boolean),
  };
}

export function buildRecipeFallbackBody(structuredContent: BlogStructuredContent): string {
  const sanitized = sanitizeStructuredContentForSave(structuredContent);
  const sections: string[] = [];

  if (sanitized.servingText) {
    sections.push(`Servings: ${sanitized.servingText}`);
  }

  if (sanitized.summaryTitle) {
    sections.push(`## ${sanitized.summaryTitle}`);
  }
  if (sanitized.summaryBody) {
    sections.push(sanitized.summaryBody);
  }

  if (sanitized.stats.length > 0) {
    sections.push(
      [
        "## At a glance",
        ...sanitized.stats.map((stat) =>
          `- ${stat.label || "Metric"}: ${stat.value || "See recipe"}${stat.secondaryValue ? ` (${stat.secondaryValue})` : ""}`,
        ),
      ].join("\n"),
    );
  }

  if (sanitized.ingredients.length > 0) {
    const ingredientLines = ["## Ingredients"];
    for (const section of sanitized.ingredients) {
      if (section.title) {
        ingredientLines.push(`### ${section.title}`);
      }
      ingredientLines.push(...section.items.map((item) => `- ${item}`));
    }
    sections.push(ingredientLines.join("\n"));
  }

  if (sanitized.directions.length > 0) {
    const directionLines = ["## Directions"];
    sanitized.directions.forEach((direction, index) => {
      const title = direction.title || `Step ${index + 1}`;
      directionLines.push(`${index + 1}. ${title}`);
      if (direction.body) {
        directionLines.push(direction.body);
      }
      if (direction.detail) {
        directionLines.push(`Note: ${direction.detail}`);
      }
    });
    sections.push(directionLines.join("\n"));
  }

  if (sanitized.notes.length > 0) {
    sections.push(["## Notes", ...sanitized.notes.map((note) => `- ${note}`)].join("\n"));
  }

  return sections.filter(Boolean).join("\n\n").trim();
}
